import React from 'react';
import { Link } from 'react-router-dom';

const Product = () => {
  const aiWorkers = [
    {
      icon: '📊',
      title: 'Claims Analysis AI Agent',
      desc: 'Monitors claim volume and type, identifies trends in medical spend and top cost drivers, and compares against industry.'
    },
    {
      icon: '📈',
      title: 'Enrollment & Engagement AI Agent',
      desc: 'Tracks vendor utilization, HRIS, and benchmarks to optimize offerings and strategy.'
    },
    {
      icon: '⚖️',
      title: 'Compliance AI Agent',
      desc: 'Monitors changes in regulations (e.g., ACA, FMLA) and keeps policies compliant.'
    },
    {
      icon: '📣',
      title: 'Outreach AI Agent',
      desc: 'Surfaces outreach opportunities, suggests personalized messaging, and optimizes communication.'
    }
  ];

  const dataSources = [
    'Company benefits offerings', 'SHRM', 'Employee engagement surveys',
    'Work calendar dates', 'Bureau of Labor', 'Medical claims', 'Data.gov',
    'Industry utilization rates', 'Reddit', 'Public benefits reports',
    'Glassdoor', 'Compliance updates', 'ResearchGate'
  ];

  const recommendations = [
    {
      icon: '📑',
      title: 'Stakeholder Reports',
      desc: 'Generate insights on engagement, utilization, and performance trends. Identify improvement areas automatically.'
    },
    {
      icon: '✉️',
      title: 'Email Campaigns',
      desc: 'Automate and personalize wellness campaigns — the right message at the right time.'
    },
    {
      icon: '📋',
      title: 'Employee Surveys & Trainings',
      desc: 'Deploy intelligent surveys and trainings; analyze responses in real time and generate strategies.'
    }
  ];

  return (
    <div className="page-content">
      {/* Hero */}
      <section className="hero">
        <div className="hero-content">
          <p className="hero-subtitle">The Avenir AI Platform</p>
          <h1 className="hero-title">
            Proactive, <span className="gradient-text">AI-Driven</span> Benefits Management
          </h1>
          <p className="hero-description">
            Let our 24/7 AI workers handle benefits administration with speed, accuracy, and reliability — so you can focus on growth, performance, and well-being.
          </p>
          <div className="hero-buttons">
            <Link to="/contact" className="btn btn-primary btn-large">Book a Demo</Link>
            <a href="#ai-workers" className="btn btn-secondary btn-large">Explore AI Workers</a>
          </div>
        </div>
        
        <div className="trust-bar">
          <p>Built by a team with a decade of big-data experience, 5+ years in AI, and 7+ years in B2B.</p>
          <p className="trust-sub">Our team brings experience from the world's most innovative enterprises to power your benefits strategy.</p>
        </div>
      </section>

      {/* AI Workers */}
      <section className="section" id="ai-workers">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            <span className="gradient-text">AI Expert</span> Workers
          </h2>
          <p className="section-description">
            Seamlessly automate benefits tasks and drive smarter decisions with specialized AI agents for compliance, claims analysis, enrollment, and personalized recommendations.
          </p>
        </div>

        <div className="workers-grid">
          {aiWorkers.map((worker, i) => (
            <div key={i} className="worker-card">
              <div className="worker-icon">{worker.icon}</div>
              <h3 className="worker-title">{worker.title}</h3>
              <p className="worker-desc">{worker.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Comparison Section */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Why <span className="gradient-text">AI Workers?</span>
          </h2>
        </div>

        <div className="comparison-grid">
          <div className="comparison-card comparison-without">
            <div className="comparison-header">
              <span className="comparison-badge comparison-badge-gray">Without AI</span>
              <p className="comparison-sub">How everyone else does it…</p>
            </div>
            <ul className="comparison-list">
              <li><span className="list-num">1</span> Waste time & energy on redundant tasks</li>
              <li><span className="list-num">2</span> Struggle to identify insights & trends</li>
              <li><span className="list-num">3</span> Miss opportunities to optimize costs & offerings</li>
              <li><span className="list-num">4</span> Overwhelmed by messy, large datasets</li>
            </ul>
            <div className="comparison-verdict verdict-bad">
              <span className="verdict-emoji">😔</span>
              <div>
                <strong>No fun</strong>
                <p>Takes too much effort, cost, and time for too little value.</p>
              </div>
            </div>
          </div>

          <div className="comparison-card comparison-with">
            <div className="comparison-header">
              <span className="comparison-badge comparison-badge-purple">With AI Workers</span>
              <p className="comparison-sub">How we do it…</p>
            </div>
            <ul className="comparison-list">
              <li><span className="list-num list-num-purple">1</span> Chat with AI workers who get you answers 24/7</li>
              <li><span className="list-num list-num-purple">2</span> Grab a coffee while AI analyzes all your data in seconds</li>
              <li><span className="list-num list-num-purple">3</span> Discover actionable insights you can use immediately</li>
            </ul>
            <div className="comparison-verdict verdict-good">
              <span className="verdict-emoji">🎉</span>
              <div>
                <strong>Very fun</strong>
                <p>Achieve standout results in benefits your employees will love.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversational Experience */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            An interactive, <span className="gradient-text">conversational</span> experience
          </h2>
          <p className="section-description">
            Transform how you manage employee benefits with interactive AI workers that analyze data, uncover trends, and streamline decision-making — in real time.
          </p>
        </div>

        <div className="conversation-demo">
          <div className="demo-question">
            <p>Can you answer benefits optimization questions?</p>
          </div>
          <div className="demo-answer">
            <p>Yes, generative conversational AI for benefits optimization is here.</p>
          </div>
          <div className="demo-examples">
            <span className="demo-chip">Generate EAP utilization for this quarter</span>
            <span className="demo-chip">Analyze my medical spend against competitors</span>
            <span className="demo-chip">Check compliance updates for this year</span>
          </div>
        </div>
      </section>

      {/* Data Sources */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Traditional EB Sources (Claims, Census, Rx…) plus <span className="gradient-text">150+</span> Data Sources
          </h2>
          <p className="section-description">
            From employee engagement surveys and public benefits reports to industry benchmarks and real-time compliance updates, our data ensures accurate, relevant, and expert insights.
          </p>
        </div>

        <div className="data-sources-scroll">
          <div className="scroll-track">
            {dataSources.map((source, i) => (
              <span key={i} className="source-chip">{source}</span>
            ))}
            {dataSources.map((source, i) => (
              <span key={`dup-${i}`} className="source-chip">{source}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Recommendations */}
      <section className="section">
        <div className="section-center" style={{ marginBottom: '4rem' }}>
          <h2 className="section-heading">
            Recommendations You Can <span className="gradient-text">Trust — and Use</span>
          </h2>
          <p className="section-description">
            Personalized recommendations built on real-time analytics for benefits strategies and employee engagement — deploy practical, impactful solutions that drive measurable results.
          </p>
        </div>

        <div className="recommendations-grid">
          {recommendations.map((rec, i) => (
            <div key={i} className="recommendation-card">
              <div className="recommendation-icon">{rec.icon}</div>
              <h3 className="recommendation-title">{rec.title}</h3>
              <p className="recommendation-desc">{rec.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="cta-wrapper">
          <div className="cta-glow"></div>
          <div className="cta-box">
            <h2 className="cta-title">Speak To A Specialist</h2>
            <p className="cta-desc">We'll learn about your priorities and show the fastest path to results.</p>
            <div className="cta-buttons">
              <Link to="/contact" className="btn btn-primary btn-large">Book Demo</Link>
              <a href="mailto:info@avenir.com.ai" className="btn btn-secondary btn-large">Email Us</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Product;